import type { ComponentProps } from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '@repo/lib/cn';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';

export function DropdownMenu(dropdownMenuProps: ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  const { ...props } = dropdownMenuProps;
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

export function DropdownMenuPortal(dropdownMenuPortalProps: ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  const { ...props } = dropdownMenuPortalProps;
  return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}

export function DropdownMenuTrigger(dropdownMenuTriggerProps: ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  const { ...props } = dropdownMenuTriggerProps;
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

export function DropdownMenuContent(dropdownMenuContentProps: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  const { className, sideOffset = 8, ...props } = dropdownMenuContentProps;
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-popover text-accent-foreground z-50 max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) min-w-36 origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-sm duration-150 data-[state=closed]:overflow-hidden',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export function DropdownMenuGroup(dropdownMenuGroupProps: ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  const { className, ...props } = dropdownMenuGroupProps;
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" className={cn('overflow-hidden p-1', className)} {...props} />;
}

export function DropdownMenuItem(
  dropdownMenuItemProps: ComponentProps<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: 'default' | 'destructive';
  }
) {
  const { className, inset, variant, ...props } = dropdownMenuItemProps;
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive-accent dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive not-data-[variant=destructive]:focus:**:text-accent-foreground group/dropdown-menu-item relative flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-1 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem(dropdownMenuCheckboxItemProps: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  const { className, children, checked, ...props } = dropdownMenuCheckboxItemProps;
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground relative flex cursor-pointer items-center gap-1.5 rounded-sm py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-60 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute right-2 flex items-center justify-center" data-slot="dropdown-menu-checkbox-item-indicator">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuRadioGroup(dropdownMenuRadioGroupProps: ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  const { className, ...props } = dropdownMenuRadioGroupProps;
  return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" className={cn('overflow-hidden p-1', className)} {...props} />;
}

export function DropdownMenuRadioItem(dropdownMenuRadioItemProps: ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  const { className, children, ...props } = dropdownMenuRadioItemProps;
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground focus:**:text-accent-foreground relative flex cursor-pointer items-center gap-1.5 rounded-sm py-1 pr-8 pl-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      {...props}
    >
      <span className="pointer-events-none absolute right-2 flex items-center justify-center" data-slot="dropdown-menu-radio-item-indicator">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

export function DropdownMenuLabel(
  dropdownMenuLabelProps: ComponentProps<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
) {
  const { className, inset, ...props } = dropdownMenuLabelProps;
  return (
    <DropdownMenuPrimitive.Label data-slot="dropdown-menu-label" data-inset={inset} className={cn('text-muted-foreground px-1.5 py-1 text-sm font-medium data-inset:pl-7', className)} {...props} />
  );
}

export function DropdownMenuSeparator(dropdownMenuSeparatorProps: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  const { className, ...props } = dropdownMenuSeparatorProps;
  return <DropdownMenuPrimitive.Separator data-slot="dropdown-menu-separator" className={cn('bg-border-primary h-px', className)} {...props} />;
}

export function DropdownMenuShortcut(dropdownMenuShortcutProps: ComponentProps<'span'>) {
  const { className, ...props } = dropdownMenuShortcutProps;
  return (
    <span data-slot="dropdown-menu-shortcut" className={cn('text-muted-foreground group-focus/dropdown-menu-item:text-accent-foreground ml-auto text-xs tracking-widest', className)} {...props} />
  );
}

export function DropdownMenuSub(dropdownMenuSubProps: ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  const { ...props } = dropdownMenuSubProps;
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}

export function DropdownMenuSubTrigger(
  dropdownMenuSubTriggerProps: ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
) {
  const { className, inset, children, ...props } = dropdownMenuSubTriggerProps;

  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-open:bg-accent/80 data-open:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-1 text-sm outline-hidden select-none data-inset:pl-7 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-3.5" />
    </DropdownMenuPrimitive.SubTrigger>
  );
}

export function DropdownMenuSubContent(dropdownMenuSubContentProps: ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  const { className, ...props } = dropdownMenuSubContentProps;
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-popover text-popover-foreground z-50 min-w-24 origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border shadow-lg duration-150',
        className
      )}
      {...props}
    />
  );
}
